"use client";

import { useEffect, useRef } from "react";
import {
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  ColorType,
  createChart,
  LineSeries,
  type CandlestickData,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type UTCTimestamp,
} from "lightweight-charts";
import { useAtomValue } from "jotai";
import type { Candle } from "@/lib/api";
import { candleColorsAtom, chartTypeAtom } from "@/lib/chart-prefs";
import { readToken, useTheme } from "@/lib/theme";

interface Props {
  candles: Candle[];
  className?: string;
}

/**
 * Lightweight Charts v5 wrapper.
 *
 * Two constraints shape this component:
 *  - The chart paints to canvas, so it cannot read CSS vars. Theme and user colors are
 *    resolved to concrete values via readToken() and re-applied through applyOptions()
 *    whenever they change: no chart teardown, no data refetch.
 *  - The series TYPE can't be swapped in place, so switching candles↔line recreates only
 *    the series (not the chart), preserving the user's pan/zoom.
 */
const PriceChart = ({ candles, className }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick" | "Bar" | "Line" | "Area"> | null>(null);

  const chartType = useAtomValue(chartTypeAtom);
  const colors = useAtomValue(candleColorsAtom);
  const { theme } = useTheme();

  // Create the chart once.
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: readToken("--chart-bg", "#141414") },
        textColor: readToken("--chart-text", "#737373"),
        attributionLogo: true, // Apache-2.0 requires the TradingView attribution. Keep it.
      },
      grid: {
        vertLines: { color: readToken("--chart-grid", "#292929") },
        horzLines: { color: readToken("--chart-grid", "#292929") },
      },
      rightPriceScale: { borderColor: readToken("--chart-grid", "#292929") },
      timeScale: {
        borderColor: readToken("--chart-grid", "#292929"),
        timeVisible: true,
      },
      autoSize: true,
      crosshair: { mode: 0 },
    });

    chartRef.current = chart;
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // Re-theme in place when the theme flips or the user edits colors.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.applyOptions({
      layout: {
        background: { type: ColorType.Solid, color: readToken("--chart-bg", "#141414") },
        textColor: readToken("--chart-text", "#737373"),
      },
      grid: {
        vertLines: { color: readToken("--chart-grid", "#292929") },
        horzLines: { color: readToken("--chart-grid", "#292929") },
      },
      rightPriceScale: { borderColor: readToken("--chart-grid", "#292929") },
      timeScale: { borderColor: readToken("--chart-grid", "#292929") },
    });
  }, [theme]);

  // Replace the series ONLY when the series TYPE changes.
  //
  // Colors and theme are deliberately NOT dependencies here. A series is created empty and
  // filled by the data effect below; if a color or theme change tore the series down, the
  // data effect (keyed on chartType, not colors) would not re-run and the chart would go
  // blank until the next refetch. Colors are applied via applyOptions instead, which is
  // what that API is for, and it repaints without touching the data or the user's zoom.
  //
  // Effect order matters and is load-bearing: within one commit React runs effects in source
  // order, so on a chartType change this rebuild runs, then the color effect styles the new
  // series, then the data effect fills it.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (seriesRef.current) {
      chart.removeSeries(seriesRef.current);
      seriesRef.current = null;
    }

    // Structural options only, anything color-related belongs in the effect below.
    if (chartType === "candles") {
      seriesRef.current = chart.addSeries(CandlestickSeries, { borderVisible: false });
    } else if (chartType === "bars") {
      seriesRef.current = chart.addSeries(BarSeries, {});
    } else if (chartType === "line") {
      seriesRef.current = chart.addSeries(LineSeries, { lineWidth: 2 });
    } else {
      seriesRef.current = chart.addSeries(AreaSeries, { lineWidth: 2 });
    }
  }, [chartType]);

  // Paint the current series. Runs on colour edits, theme flips, and immediately after a
  // rebuild, never destroys anything, so the data and viewport survive.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    if (chartType === "candles") {
      (series as ISeriesApi<"Candlestick">).applyOptions({
        upColor: colors.up,
        downColor: colors.down,
        wickUpColor: colors.wickUp,
        wickDownColor: colors.wickDown,
      });
    } else if (chartType === "bars") {
      (series as ISeriesApi<"Bar">).applyOptions({
        upColor: colors.up,
        downColor: colors.down,
      });
    } else if (chartType === "line") {
      (series as ISeriesApi<"Line">).applyOptions({
        color: readToken("--brand-primary", colors.up),
      });
    } else {
      const line = readToken("--brand-primary", colors.up);
      (series as ISeriesApi<"Area">).applyOptions({
        lineColor: line,
        // Tokens are hsl(...) so alpha composes by rewriting the closing paren.
        topColor: line.replace(")", " / 0.35)"),
        bottomColor: line.replace(")", " / 0.02)"),
      });
    }
    // `theme` is not read directly, it's the signal that readToken()'s values changed.
  }, [colors, chartType, theme]);

  // Feed data. OHLC series want {open,high,low,close}; line/area want a single value, 
  // so narrow to the concrete series variant rather than casting the payload.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    if (chartType === "candles" || chartType === "bars") {
      const data: CandlestickData<UTCTimestamp>[] = candles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
      }));
      (series as ISeriesApi<"Candlestick" | "Bar">).setData(data);
    } else {
      const data: LineData<UTCTimestamp>[] = candles.map((c) => ({
        time: c.time as UTCTimestamp,
        value: Number(c.close),
      }));
      (series as ISeriesApi<"Line" | "Area">).setData(data);
    }

    chartRef.current?.timeScale().fitContent();
  }, [candles, chartType]);

  return <div ref={containerRef} className={className} />;
};

export default PriceChart;
